import { VYLABS_BASE_URL, RENEW_ACCESS_TOKEN } from "./url_helper";
import { post } from "./Api_helper";
import Cookies from "js-cookie";
import { TENANT_OTP_API_KEY } from "../Tenant-Config/TenantConfig";

const isTokenExpired = (expiryDate) => {
  if (!expiryDate) return true;
  return new Date(expiryDate) < new Date();
};

const scheduleTokenCheck = (expiryDate) => {
  const expiryTime = new Date(expiryDate).getTime();
  const currentTime = new Date().getTime();
  const timeUntilExpiry = expiryTime - currentTime;
  const refreshBuffer = 60000;
  const timeUntilRefresh = timeUntilExpiry - refreshBuffer;

  if (timeUntilRefresh > 0) {
    setTimeout(() => {
      checkAndRefreshToken(true);
    }, timeUntilRefresh);
  } else if (timeUntilExpiry > 0) {
    checkAndRefreshToken(true);
  } else {
    checkAndRefreshToken();
  }
};

const checkAndRefreshToken = async (forceRefresh = false) => {
  const accessTokenExpiry = localStorage.getItem("tokenExpiry");
  const refreshToken = Cookies.get("refreshToken");
  const refreshTokenExpiry = Cookies.get("refreshTokenExpiry");
  if (forceRefresh || isTokenExpired(accessTokenExpiry)) {
    if (refreshToken && !isTokenExpired(refreshTokenExpiry)) {
      const success = await refreshAuthToken();
      if (!success) {
        if (localStorage.getItem("sessionExpiredReason") !== "blocked") {
          handleFailedRefresh("refresh failed");
        }
      }
    } else {
      handleFailedRefresh("no valid refresh token");
    }
  }
};

let isRefreshing = null;
const refreshAuthToken = async () => {
  if (isRefreshing) {
    return isRefreshing;
  }
  isRefreshing = (async () => {
    try {
      const refreshToken = Cookies.get("refreshToken");

      if (!refreshToken) {
        console.error("No refresh token available in cookies");
        return false;
      }

      const response = await post(
        VYLABS_BASE_URL,
        RENEW_ACCESS_TOKEN,
        {
          refreshToken: refreshToken,
        },
        { 
          _skipAuth: true,  
          headers: {
            "X-API-Key": TENANT_OTP_API_KEY,
          },
        }
      );

      const { access_token, access_token_expires_at } = response || {};

      if (!access_token) {
        throw new Error("No access token in response");
      }

      localStorage.setItem("token", access_token);
      localStorage.setItem("tokenExpiry", access_token_expires_at);
      scheduleTokenCheck(access_token_expires_at);
      return true;
    } catch (error) {
      console.error("Token refresh failed:", error);
      const errorData = error.response?.data;
      let isBlockedSession = false;
      if (typeof errorData === "string") {
        isBlockedSession = errorData.toLowerCase().includes("blocked session");
      } else if (errorData && typeof errorData === "object") {
        isBlockedSession =
          errorData.message?.toLowerCase().includes("blocked session") ||
          errorData.error?.toLowerCase().includes("blocked session");
      }
      if (isBlockedSession) {
        handleBlockedSession();
        return false;
      }

      const isAuthFailure = error.response?.status === 401;

      if (isAuthFailure) {
        handleFailedRefresh("refresh token invalid");
        return false;
      } else {
        if (error.response?.status >= 500) {
          return false;
        }
        handleFailedRefresh(`server error: ${error.response?.status}`);
        return false;
      }
    } finally {
      isRefreshing = null;
    }
  })();
  return isRefreshing;
};

const isAuthenticated = () => {
  const refreshToken = Cookies.get("refreshToken");
  const refreshTokenExpiry = Cookies.get("refreshTokenExpiry");
  return (
    refreshToken && refreshTokenExpiry && !isTokenExpired(refreshTokenExpiry)
  );
};

const handleFailedRefresh = (reason = "unknown") => {
  localStorage.setItem("sessionExpired", "true");
  localStorage.setItem("sessionExpiredReason", "expired");
  window.dispatchEvent(new Event("session-expired"));
};

const handleBlockedSession = () => {
  localStorage.setItem("sessionExpired", "true");
  localStorage.setItem("sessionExpiredReason", "blocked");
  window.dispatchEvent(new Event("session-expired"));
};

export {
  refreshAuthToken,
  isTokenExpired,
  scheduleTokenCheck,
  isAuthenticated,
  handleFailedRefresh,
  handleBlockedSession,
  checkAndRefreshToken,
};
